import { useState, useCallback, type FormEvent, type ReactNode } from 'react';
import { CATEGORIES, CONDITIONS, CURRENCIES, VIDEO_SYSTEMS, CATEGORY_PLATFORMS, PLATFORMS } from '@siliconvault/shared';
import type { CreateInventoryItemInput, VaultTypeDefinition } from '@siliconvault/shared';
import { Input, Textarea, Select } from '../ui/Input';
import { ComboBox } from '../ui/ComboBox';
import { SelectWithOther } from '../ui/SelectWithOther';
import { Button } from '../ui/Button';
import { ChevronRight, Plus, X } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { useTaxonomySuggestions } from '../../hooks/useTaxonomy';
import { useVault } from '../../context/VaultContext';
import { usePlatformLabel } from '../../hooks/usePlatformLabel';
import styles from './ItemForm.module.css';

interface ItemFormProps {
  initialData?: Partial<CreateInventoryItemInput>;
  onSubmit: (data: CreateInventoryItemInput) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  submitLabel?: string;
  renderScanner?: ReactNode;
  scannerSlotRef?: (el: HTMLDivElement | null) => void;
}

export function ItemForm({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = 'Save Item',
  renderScanner,
  scannerSlotRef,
}: ItemFormProps) {
  const { t } = useTranslation();
  const { vaultTypeDef } = useVault();
  const platformLabel = usePlatformLabel();

  // Use vault type definitions when available, otherwise fall back to retro computer defaults.
  // For custom vaults with empty arrays, keep them empty so the form uses free-text inputs.
  const vtCategories = (vaultTypeDef?.categories?.length ?? 0) > 0 ? vaultTypeDef!.categories : (vaultTypeDef ? [] : CATEGORIES);
  const vtBrands = (vaultTypeDef?.brands?.length ?? 0) > 0 ? vaultTypeDef!.brands : (vaultTypeDef ? [] : PLATFORMS);
  const vtCategoryBrands = vaultTypeDef?.categoryBrands ?? (vaultTypeDef ? {} : CATEGORY_PLATFORMS);
  const vtSubcategories = vaultTypeDef?.subcategories ?? {};
  const vtSpecializedFields = vaultTypeDef?.specializedFields ?? [];
  const vtFeatures = vaultTypeDef?.specializedFeatures ?? {};

  const defaultCategory = (initialData.category ?? (vtCategories.length > 0 ? vtCategories[0] : '') ?? '') as string;
  const defaultBrand = (initialData.platform ?? '') as string;
  const showVideoSystem = vtFeatures.video_system === true;

  // Vault-aware placeholders
  const vaultPlaceholders: Record<string, { name: string; subcategory: string; cpu: string; memory: string; launchDate: string }> = {
    retro_computers: {
      name: t('itemForm.namePlaceholder'),
      subcategory: t('itemForm.subcategoryPlaceholder'),
      cpu: t('itemForm.cpuPlaceholder'),
      memory: t('itemForm.memoryPlaceholder'),
      launchDate: t('itemForm.launchDatePlaceholder'),
    },
    retro_audio: {
      name: 'e.g., Pioneer SX-1250',
      subcategory: 'e.g., Stereo, Belt Drive, Bookshelf',
      cpu: '', memory: '',
      launchDate: 'e.g., 1975',
    },
    vintage_cameras: {
      name: 'e.g., Nikon F2 Photomic',
      subcategory: 'e.g., 35mm, Wide Angle, Filter',
      cpu: '', memory: '',
      launchDate: 'e.g., 1971',
    },
    watches: {
      name: 'e.g., Omega Speedmaster 3570.50',
      subcategory: 'e.g., Professional, Ultra-Thin, Manual Wind',
      cpu: '', memory: '',
      launchDate: 'e.g., 1957',
    },
  };
  const genericPlaceholders = {
    name: 'e.g., Item Name',
    subcategory: 'e.g., Sub-type or variant',
    cpu: '', memory: '',
    launchDate: 'e.g., 2000',
  };
  const ph = vaultTypeDef
    ? (vaultPlaceholders[vaultTypeDef.id] ?? genericPlaceholders)
    : vaultPlaceholders.retro_computers;

  const [name, setName] = useState(initialData.name ?? '');
  const [category, setCategory] = useState<string>(defaultCategory);
  const [platform, setPlatform] = useState<string>(defaultBrand);
  const [subcategory, setSubcategory] = useState<string>((initialData as any).subcategory ?? '');
  const [condition, setCondition] = useState<string>(initialData.condition ?? 'good');
  const [description, setDescription] = useState(initialData.description ?? '');
  const [acquisitionDate, setAcquisitionDate] = useState(
    initialData.acquisitionDate ?? ''
  );
  const [acquisitionPrice, setAcquisitionPrice] = useState(
    initialData.acquisitionPrice ?? ''
  );
  const [currency, setCurrency] = useState<string>(initialData.currency ?? 'USD');
  const [videoSystem, setVideoSystem] = useState<string>((initialData as any).videoSystem ?? '');
  const [location, setLocation] = useState<string>((initialData as any).location ?? '');

  // Extended metadata fields
  const meta = (initialData as any)?.metadata ?? {};
  const [cpu, setCpu] = useState<string>(meta.cpu ?? '');
  const [memory, setMemory] = useState<string>(meta.memory ?? '');
  const [launchDate, setLaunchDate] = useState<string>(meta.launchDate ?? '');
  const [launchPrice, setLaunchPrice] = useState<string>(meta.launchPrice ?? '');
  const [launchPriceCurrency, setLaunchPriceCurrency] = useState<string>(meta.launchPriceCurrency ?? 'USD');
  const [launchPriceInflationAdjusted, setLaunchPriceInflationAdjusted] = useState<string>(meta.launchPriceInflationAdjusted ?? '');
  const [customNotes, setCustomNotes] = useState<string>(meta.customNotes ?? '');

  // Vault-type specialized fields — stored in metadata with their key names
  const [specializedValues, setSpecializedValues] = useState<Record<string, string>>(() => {
    const values: Record<string, string> = {};
    for (const field of vtSpecializedFields) {
      // Skip cpu/memory since they have dedicated state already
      if (field.key === 'cpu' || field.key === 'memory') continue;
      values[field.key] = (meta[field.key] as string) ?? '';
    }
    return values;
  });

  // Custom fields (key-value pairs stored in metadata)
  const initialCustomFields: Array<{key: string, value: string}> = Array.isArray(meta.customFields) ? meta.customFields : [];
  const [customFields, setCustomFields] = useState<Array<{key: string, value: string}>>(() => initialCustomFields);
  const [customFieldsOpen, setCustomFieldsOpen] = useState(() => initialCustomFields.length > 0);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Taxonomy suggestions (only subcategory uses the API; category & platform use static constants)
  const { suggestions: subcategorySuggestions } = useTaxonomySuggestions('subcategory', category, platform);

  // When category changes, reset platform to first valid option and clear subcategory.
  const handleCategoryChange = useCallback((newCategory: string) => {
    setCategory(newCategory);
    if ((vtCategories as readonly string[]).includes(newCategory)) {
      const platforms = vtCategoryBrands[newCategory] ?? [...vtBrands];
      setPlatform(platforms[0] ?? '');
      setSubcategory('');
    }
  }, [vtCategories, vtCategoryBrands, vtBrands]);

  // Platform options depend on the selected category
  const platformOptions = vtCategoryBrands[category] ?? [...vtBrands];

  // Track unsaved changes
  const isDirty =
    name !== (initialData.name ?? '') ||
    category !== defaultCategory ||
    platform !== defaultBrand ||
    subcategory !== ((initialData as any).subcategory ?? '') ||
    condition !== (initialData.condition ?? 'good') ||
    description !== (initialData.description ?? '') ||
    acquisitionDate !== (initialData.acquisitionDate ?? '') ||
    acquisitionPrice !== (initialData.acquisitionPrice ?? '') ||
    currency !== (initialData.currency ?? 'USD') ||
    videoSystem !== ((initialData as any).videoSystem ?? '') ||
    location !== ((initialData as any).location ?? '') ||
    cpu !== (meta.cpu ?? '') ||
    memory !== (meta.memory ?? '') ||
    launchDate !== (meta.launchDate ?? '') ||
    launchPrice !== (meta.launchPrice ?? '') ||
    launchPriceCurrency !== (meta.launchPriceCurrency ?? 'USD') ||
    launchPriceInflationAdjusted !== (meta.launchPriceInflationAdjusted ?? '') ||
    customNotes !== (meta.customNotes ?? '') ||
    JSON.stringify(customFields) !== JSON.stringify(initialCustomFields);

  useUnsavedChanges(isDirty);

  function addCustomField() {
    if (customFields.length >= 10) return;
    setCustomFields(prev => [...prev, { key: '', value: '' }]);
    setCustomFieldsOpen(true);
  }

  function removeCustomField(index: number) {
    setCustomFields(prev => prev.filter((_, i) => i !== index));
  }

  function updateCustomField(index: number, field: 'key' | 'value', val: string) {
    setCustomFields(prev => prev.map((cf, i) => i === index ? { ...cf, [field]: val } : cf));
  }

  const validate = useCallback(
    (fields?: { name?: string; acquisitionPrice?: string }) => {
      const n = fields?.name ?? name;
      const ap = fields?.acquisitionPrice ?? acquisitionPrice;
      const newErrors: Record<string, string> = {};

      if (!n.trim()) newErrors.name = t('itemForm.nameRequired');
      if (ap && !/^\d{1,8}(\.\d{1,2})?$/.test(ap)) {
        newErrors.acquisitionPrice = t('itemForm.invalidPrice');
      }

      return newErrors;
    },
    [name, acquisitionPrice, t]
  );

  function handleBlur(field: string) {
    setTouched((prev) => new Set(prev).add(field));
    const allErrors = validate();
    setErrors((prev) => {
      const next = { ...prev };
      if (allErrors[field]) {
        next[field] = allErrors[field];
      } else {
        delete next[field];
      }
      return next;
    });
  }

  function handleFieldChange(field: string, value: string, setter: (v: string) => void) {
    setter(value);
    if (touched.has(field)) {
      const allErrors = validate({ [field]: value });
      setErrors((prev) => {
        const next = { ...prev };
        if (allErrors[field]) {
          next[field] = allErrors[field];
        } else {
          delete next[field];
        }
        return next;
      });
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setTouched(new Set(['name', 'acquisitionPrice']));
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSubmit({
      name: name.trim(),
      category,
      platform,
      condition: condition as typeof CONDITIONS[number],
      quantity: initialData.quantity ?? 1,
      description,
      acquisitionDate: acquisitionDate || null,
      acquisitionPrice: acquisitionPrice || null,
      currency: currency as typeof CURRENCIES[number],
      videoSystem: videoSystem ? videoSystem as typeof VIDEO_SYSTEMS[number] : null,
      subcategory: subcategory.trim() || null,
      location: location.trim() || null,
      metadata: {
        ...(cpu.trim() ? { cpu: cpu.trim() } : {}),
        ...(memory.trim() ? { memory: memory.trim() } : {}),
        ...(launchDate.trim() ? { launchDate: launchDate.trim() } : {}),
        ...(launchPrice.trim() ? { launchPrice: launchPrice.trim() } : {}),
        ...(launchPrice.trim() && launchPriceCurrency ? { launchPriceCurrency } : {}),
        ...(launchPriceInflationAdjusted.trim() ? { launchPriceInflationAdjusted: launchPriceInflationAdjusted.trim() } : {}),
        ...(customNotes.trim() ? { customNotes: customNotes.trim() } : {}),
        // Vault-type specialized fields
        ...Object.fromEntries(
          Object.entries(specializedValues)
            .filter(([, v]) => v.trim())
            .map(([k, v]) => [k, v.trim()])
        ),
        ...((() => {
          const nonEmpty = customFields.filter(cf => cf.key.trim() && cf.value.trim());
          return nonEmpty.length > 0 ? { customFields: nonEmpty.map(cf => ({ key: cf.key.trim(), value: cf.value.trim() })) } : {};
        })()),
      },
    });
  }

  // Show CPU/Memory fields only when vault type explicitly includes them
  const hasCpuField = vtSpecializedFields.some((f) => f.key === 'cpu');
  const hasMemoryField = vtSpecializedFields.some((f) => f.key === 'memory');
  const showHardwareFields = vaultTypeDef
    ? (hasCpuField || hasMemoryField)
    : (category === 'Computer' || category === 'Console'); // legacy fallback

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {/* Top section: scanner integrated or plain fields */}
      {(renderScanner || scannerSlotRef) ? (
        <div className={styles.topGrid}>
          <div className={styles.scannerCell} ref={scannerSlotRef}>
            {renderScanner}
          </div>
          {/* Col 2: Name, Platform, Video System */}
          <Input
            label={t('itemForm.name')}
            value={name}
            onChange={(e) => handleFieldChange('name', e.target.value, setName)}
            onBlur={() => handleBlur('name')}
            placeholder={ph.name}
            error={errors.name}
            required
          />
          {/* Col 3: Category */}
          {vtCategories.length > 0 ? (
            <SelectWithOther
              label={t('itemForm.category')}
              value={category}
              onChange={handleCategoryChange}
              options={vtCategories}
              placeholder={t('itemForm.otherPlaceholder')}
              required
            />
          ) : (
            <Input
              label={t('itemForm.category')}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Type, Genre, Style"
              required
            />
          )}
          {/* Col 2: Platform */}
          {platformOptions.length > 0 ? (
            <SelectWithOther
              label={platformLabel}
              value={platform}
              onChange={setPlatform}
              options={platformOptions}
              placeholder={t('itemForm.otherPlaceholder')}
              required
            />
          ) : (
            <Input
              label={platformLabel}
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              placeholder={`e.g. ${vaultTypeDef?.platformLabel ?? 'Brand'}`}
              required
            />
          )}
          {/* Col 3: Subcategory */}
          <ComboBox
            label={t('itemForm.subcategory')}
            value={subcategory}
            onChange={setSubcategory}
            suggestions={subcategorySuggestions}
            placeholder={ph.subcategory}
          />
          {/* Col 2: Video System (only for vault types with this feature) */}
          {showVideoSystem && (
            <Select
              label={t('itemForm.videoSystem')}
              value={videoSystem}
              onChange={(e) => setVideoSystem(e.target.value)}
            >
              <option value="">—</option>
              {VIDEO_SYSTEMS.map((vs) => (
                <option key={vs} value={vs}>
                  {t(`videoSystem.${vs}` as any)}
                </option>
              ))}
            </Select>
          )}
          {/* Col 3: Condition */}
          <Select
            label={t('itemForm.condition')}
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
          >
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {t(`condition.${c}` as any)}
              </option>
            ))}
          </Select>
        </div>
      ) : (
        <>
          <Input
            label={t('itemForm.name')}
            value={name}
            onChange={(e) => handleFieldChange('name', e.target.value, setName)}
            onBlur={() => handleBlur('name')}
            placeholder={ph.name}
            error={errors.name}
            required
          />
          <div className={styles.row3}>
            {vtCategories.length > 0 ? (
              <SelectWithOther
                label={t('itemForm.category')}
                value={category}
                onChange={handleCategoryChange}
                options={vtCategories}
                placeholder={t('itemForm.otherPlaceholder')}
                required
              />
            ) : (
              <Input
                label={t('itemForm.category')}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Type, Genre, Style"
                required
              />
            )}
            {platformOptions.length > 0 ? (
              <SelectWithOther
                label={platformLabel}
                value={platform}
                onChange={setPlatform}
                options={platformOptions}
                placeholder={t('itemForm.otherPlaceholder')}
                required
              />
            ) : (
              <Input
                label={platformLabel}
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                placeholder={`e.g. ${vaultTypeDef?.platformLabel ?? 'Brand'}`}
                required
              />
            )}
            <ComboBox
              label={t('itemForm.subcategory')}
              value={subcategory}
              onChange={setSubcategory}
              suggestions={subcategorySuggestions}
              placeholder={ph.subcategory}
            />
          </div>
          <div className={styles.row3}>
            <Select
              label={t('itemForm.condition')}
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
            >
              {CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {t(`condition.${c}` as any)}
                </option>
              ))}
            </Select>
            {showVideoSystem && (
              <Select
                label={t('itemForm.videoSystem')}
                value={videoSystem}
                onChange={(e) => setVideoSystem(e.target.value)}
              >
                <option value="">—</option>
                {VIDEO_SYSTEMS.map((vs) => (
                  <option key={vs} value={vs}>
                    {t(`videoSystem.${vs}` as any)}
                  </option>
                ))}
              </Select>
            )}
            <Input
              label={t('itemForm.location')}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t('itemForm.locationPlaceholder')}
            />
          </div>
        </>
      )}

      {/* CPU | Memory (only for vault types with hardware fields) */}
      {showHardwareFields && (
        <div className={styles.row3}>
          {hasCpuField && (
            <Input
              label={t('itemForm.cpu')}
              value={cpu}
              onChange={(e) => setCpu(e.target.value)}
              placeholder={ph.cpu}
            />
          )}
          {hasMemoryField && (
            <Input
              label={t('itemForm.memory')}
              value={memory}
              onChange={(e) => setMemory(e.target.value)}
              placeholder={ph.memory}
            />
          )}
        </div>
      )}

      {/* Vault-type specialized fields (excluding cpu/memory which are above) */}
      {(() => {
        const fields = vtSpecializedFields.filter((f) => f.key !== 'cpu' && f.key !== 'memory');
        if (fields.length === 0) return null;
        // Chunk into rows of 3
        const rows: typeof fields[] = [];
        for (let i = 0; i < fields.length; i += 3) {
          rows.push(fields.slice(i, i + 3));
        }
        return rows.map((row, ri) => (
          <div key={ri} className={styles.row3}>
            {row.map((field) => {
              const val = specializedValues[field.key] ?? '';
              const setVal = (v: string) =>
                setSpecializedValues((prev) => ({ ...prev, [field.key]: v }));

              if (field.type === 'select' && field.options) {
                return (
                  <Select
                    key={field.key}
                    label={field.label}
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                  >
                    <option value="">—</option>
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </Select>
                );
              }

              if (field.type === 'boolean') {
                return (
                  <Select
                    key={field.key}
                    label={field.label}
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                  >
                    <option value="">—</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Select>
                );
              }

              return (
                <Input
                  key={field.key}
                  label={field.label}
                  type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'url' ? 'url' : 'text'}
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  placeholder={field.placeholder}
                />
              );
            })}
          </div>
        ));
      })()}

      {/* Launch Date | Launch Price | Currency */}
      <div className={styles.row3}>
        <Input
          label={t('itemForm.launchDate')}
          value={launchDate}
          onChange={(e) => setLaunchDate(e.target.value)}
          placeholder={ph.launchDate}
        />
        <Input
          label={t('itemForm.launchPrice')}
          value={launchPrice}
          onChange={(e) => setLaunchPrice(e.target.value)}
          placeholder="0.00"
        />
        <Select
          label={t('itemForm.launchPriceCurrency')}
          value={launchPriceCurrency}
          onChange={(e) => setLaunchPriceCurrency(e.target.value)}
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </div>

      {/* Inflation-adjusted price — conditional, read-only */}
      {launchPriceInflationAdjusted && (
        <Input
          label={t('itemForm.inflationAdjustedPrice')}
          value={`$${launchPriceInflationAdjusted}`}
          onChange={() => {}}
          readOnly
        />
      )}

      {/* Acq Date | Acq Price | Currency */}
      <div className={styles.row3}>
        <Input
          label={t('itemForm.acquisitionDate')}
          type="date"
          value={acquisitionDate}
          onChange={(e) => setAcquisitionDate(e.target.value)}
        />
        <Input
          label={t('itemForm.acquisitionPrice')}
          value={acquisitionPrice}
          onChange={(e) =>
            handleFieldChange('acquisitionPrice', e.target.value, setAcquisitionPrice)
          }
          onBlur={() => handleBlur('acquisitionPrice')}
          placeholder="0.00"
          error={errors.acquisitionPrice}
        />
        <Select
          label={t('itemForm.currency')}
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
        >
          {CURRENCIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </div>

      {/* Description | Custom Notes — 2-col */}
      <div className={styles.row}>
        <Textarea
          label={t('itemForm.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('itemForm.descriptionPlaceholder')}
          rows={2}
        />
        <Textarea
          label={t('itemForm.customNotes')}
          value={customNotes}
          onChange={(e) => setCustomNotes(e.target.value)}
          placeholder={t('itemForm.customNotesPlaceholder')}
          rows={2}
        />
      </div>

      {/* Custom Fields */}
      <div className={styles.customFieldsSection}>
        <button
          type="button"
          className={styles.customFieldsHeader}
          onClick={() => setCustomFieldsOpen(prev => !prev)}
        >
          <ChevronRight size={16} className={customFieldsOpen ? styles.chevronOpen : ''} />
          <span>{t('itemForm.customFields')} ({customFields.length}/10)</span>
          <span
            className={styles.addFieldBtn}
            role="button"
            onClick={(e) => { e.stopPropagation(); addCustomField(); }}
            aria-disabled={customFields.length >= 10}
          >
            <Plus size={14} />
            {t('itemForm.addCustomField')}
          </span>
        </button>
        {customFieldsOpen && customFields.length > 0 && (
          <div className={styles.customFieldsBody}>
            {customFields.map((cf, i) => (
              <div key={i} className={styles.customFieldRow}>
                <input
                  className={styles.customFieldInput}
                  value={cf.key}
                  onChange={(e) => updateCustomField(i, 'key', e.target.value)}
                  placeholder={t('itemForm.customFieldName')}
                />
                <input
                  className={styles.customFieldInput}
                  value={cf.value}
                  onChange={(e) => updateCustomField(i, 'value', e.target.value)}
                  placeholder={t('itemForm.customFieldValue')}
                />
                <button
                  type="button"
                  className={styles.customFieldRemoveBtn}
                  onClick={() => removeCustomField(i)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <Button type="submit" loading={isLoading}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t('itemForm.cancel')}
          </Button>
        )}
      </div>
    </form>
  );
}
